import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, setAuthToken, clearAuthToken, getCurrentUser, setCurrentUser } from '../services/api';
import { toast } from 'sonner';
import { trackLogin } from '../utils/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check for existing auth on mount (supports both sessionStorage and localStorage)
  useEffect(() => {
    const initAuth = async () => {
      // Check sessionStorage first (current session), then localStorage (remember me)
      const token = sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
      const storedUser = getCurrentUser(); // Already handles both storages
      
      console.log('AuthContext init:', { hasToken: !!token, hasUser: !!storedUser });
      
      if (token && storedUser) {
        // Set user as authenticated with stored data
        setUser(storedUser);
        setIsAuthenticated(true);
        console.log('AuthContext: User authenticated:', storedUser.name);
      } else {
        console.log('AuthContext: No valid auth found');
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials) => {
    try {
      setIsLoading(true);
      const response = await authAPI.login(credentials);
      
      // The API interceptor returns full response, so we need response.data
      const responseData = response.data || response;
      const { access_token, user: userData } = responseData;
      
      if (!access_token || !userData) {
        throw new Error('Invalid response: missing access_token or user data');
      }
      
      setAuthToken(access_token);
      setCurrentUser(userData);
      setUser(userData);
      setIsAuthenticated(true);
      
      // Track login event for analytics
      trackLogin(userData.email);
      
      toast.success('Login successful!');
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed: ' + (error.message || 'Unknown error'));
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setIsLoading(true);
      const response = await authAPI.register(userData);
      
      // Backend now returns Token (access_token + user) for automatic login
      const { access_token, user } = response.data;
      
      if (access_token && user) {
        // Store token and set user state for automatic login
        setAuthToken(access_token);
        setUser(user);
        setCurrentUser(user);
        setIsAuthenticated(true);
        
        toast.success('Registration successful! Please complete your profile. Check your email to verify your account.');
        return { success: true, user };
      } else {
        // Fallback for old API response format
        const user = response.data || response;
        toast.success('Registration successful! Please check your email to verify your account.');
        return { success: true, user };
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Ignore logout errors
    } finally {
      clearAuthToken();
      setUser(null);
      setIsAuthenticated(false);
      toast.success('Logged out successfully');
    }
  };

  const fetchUser = async () => {
    try {
      const token = sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
      if (!token) {
        return { success: false, error: 'No auth token' };
      }
      
      const response = await authAPI.getCurrentUser();
      const userData = response.data;
      
      setUser(userData);
      setCurrentUser(userData);
      setIsAuthenticated(true);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('AuthContext: Fetch user failed:', error);
      return { success: false, error: error.message };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      console.log('AuthContext: Updating profile with data:', profileData);
      const response = await authAPI.updateProfile(profileData);
      console.log('AuthContext: Profile update successful:', response.data);
      setUser(response.data);
      setCurrentUser(response.data);
      toast.success('Profile updated successfully');
      return { success: true };
    } catch (error) {
      console.error('AuthContext: Profile update failed:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      const message = error.response?.data?.detail || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    fetchUser,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};